import { Anchor, Group, Text, Tooltip } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { brand } from '@/theme/theme';
import { FailureBadge } from './FailureBadge';
import { firstLine, runFailure } from './runError';

/** What a run row needs to say why it failed. */
export interface FailedRunLike {
  public_id: string;
  state?: string | null;
  error?: unknown;
  forun_url?: string | null;
}

/**
 * Why a run failed, in one line, for staff lists: forun's category, the code and the first line
 * of the message, with the whole message (and hint) in a tooltip. A failed run without a stored
 * reason says so and links its logs and its page in forun. Nothing for a run that has not failed.
 * The summary clamps to `lines`, at most `maw` px wide, and breaks anywhere, so a long code or path
 * never widens a table.
 */
export function RunErrorLine({ run, lines = 1, maw = 420 }: { run: FailedRunLike; lines?: number; maw?: number }) {
  const { t } = useTranslation('runs');
  const failure = runFailure(run.error);
  if (failure && (failure.code || failure.message)) {
    const summary = [failure.code, firstLine(failure.message)].filter(Boolean).join(': ');
    const full = [failure.message, failure.hint && `${t('failure.hint')}: ${failure.hint}`].filter(Boolean).join('\n\n');
    return (
      <Group gap={6} wrap="nowrap" align="flex-start" maw={maw} miw={0}>
        <FailureBadge category={failure.category} />
        <Tooltip label={full || summary} multiline maw={480} style={{ whiteSpace: 'pre-wrap' }}>
          <Text fz={12} c="red" lineClamp={lines} miw={0} style={{ overflowWrap: 'anywhere' }}>
            {summary}
          </Text>
        </Tooltip>
      </Group>
    );
  }
  if (run.state !== 'failed') return null;
  return (
    <Group gap={6} wrap="wrap">
      <Text fz={12} c={brand.textMuted}>
        {t('failure.noReason')}
      </Text>
      <Link to="/staff/runs/$runId" params={{ runId: run.public_id }} search={{ tab: 'logs' }} style={{ fontSize: 12, color: brand.text }}>
        {t('failure.logs')}
      </Link>
      {run.forun_url && (
        <Anchor href={run.forun_url} target="_blank" rel="noopener" fz={12}>
          {t('failure.forun')}
        </Anchor>
      )}
    </Group>
  );
}
